import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, Pencil } from 'lucide-react';
import ErrorModal from '@/components/ErrorModal';

const FLAG_URL = (code) => `https://flagcdn.com/w40/${code?.toLowerCase()}.png`;

export default function MatchesAdmin() {
  const [matches, setMatches] = useState([]);
  const [countries, setCountries] = useState([]);
  const [stages, setStages] = useState([]);
  const [stadiums, setStadiums] = useState([]);
  const [venues, setVenues] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [form, setForm] = useState({ stage_id: '', home_team_id: '', away_team_id: '', stadium_id: '', venue_id: '', date: '', time: '', status: 'pendiente', favorite_home: false, favorite_away: false });
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  
  // Error modal state
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: 'Error',
    message: ''
  });

  const load = () => {
    Promise.all([
      api.get('/matches/enriched'),
      api.get('/countries'),
      api.get('/stages'),
      api.get('/stadiums'),
      api.get('/venues'),
    ]).then(([m, c, s, st, v]) => {
      setMatches(m.data); setCountries(c.data); setStages(s.data); setStadiums(st.data); setVenues(v.data);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleSave = async () => {
    // Validación frontend: detectar campos faltantes específicos
    const missing = [];
    if (!form.stage_id) missing.push('Etapa del torneo');
    if (!form.home_team_id) missing.push('Equipo 1');
    if (!form.away_team_id) missing.push('Equipo 2');
    if (!form.date) missing.push('Fecha');
    if (!form.time) missing.push('Hora');

    if (missing.length > 0) {
      setErrorModal({
        open: true,
        title: 'Campos requeridos faltantes',
        message: missing.map(f => `Falta: ${f}`)
      });
      return;
    }

    // Validación: no permitir el mismo equipo en ambos lados
    if (form.home_team_id === form.away_team_id) {
      setErrorModal({
        open: true,
        title: 'Equipos inválidos',
        message: 'Un equipo no puede jugar contra sí mismo. Por favor, selecciona dos equipos diferentes.'
      });
      return;
    }

    try {
      if (editingMatch) {
        // Actualizar partido existente
        await api.put(`/matches/${editingMatch}`, form);
        toast.success('Partido actualizado');
      } else {
        // Crear nuevo partido
        await api.post('/matches', form);
        toast.success('Partido creado');
      }
      setShowForm(false);
      setEditingMatch(null);
      setForm({ stage_id: '', home_team_id: '', away_team_id: '', stadium_id: '', venue_id: '', date: '', time: '', status: 'pendiente', favorite_home: false, favorite_away: false });
      load();
    } catch (err) { 
      const errorMessage = err.response?.data?.detail || (editingMatch ? 'Error al actualizar partido' : 'Error al crear partido');
      setErrorModal({
        open: true,
        title: editingMatch ? 'Error al actualizar partido' : 'Error al crear partido',
        message: errorMessage
      });
    }
  };

  const handleEdit = (match) => {
    setEditingMatch(match.id);
    setForm({
      stage_id: match.stage?.id || '',
      home_team_id: match.home_team?.id || '',
      away_team_id: match.away_team?.id || '',
      stadium_id: match.stadium?.id || '',
      venue_id: match.venue?.id || '',
      date: match.date || '',
      time: match.time || '',
      status: match.status || 'pendiente',
      favorite_home: match.favorite_home || false,
      favorite_away: match.favorite_away || false
    });
    setShowForm(true);
  };

  const handleCreateNew = () => {
    setEditingMatch(null);
    setForm({ stage_id: '', home_team_id: '', away_team_id: '', stadium_id: '', venue_id: '', date: '', time: '', status: 'pendiente' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar partido?')) return;
    await api.delete(`/matches/${id}`);
    toast.success('Partido eliminado');
    load();
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-matches-page">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-black gold-text text-center lg:text-left" style={{ fontFamily: 'Montserrat' }}>Partidos</h2>
        <button onClick={handleCreateNew} className="gold-btn px-4 py-2 rounded-lg text-sm flex items-center gap-2" data-testid="create-match-button">
          <Plus size={16} /> Crear
        </button>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider">Estado</label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="bg-[#0A0F1C] border-[#F4E39E]/40 text-white mt-1" data-testid="filter-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
              <SelectItem value="all" className="text-white">Todos los partidos</SelectItem>
              <SelectItem value="pendiente" className="text-white">Pendientes por jugar</SelectItem>
              <SelectItem value="finalizado" className="text-white">Finalizados</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wider">Etapa</label>
          <Select value={filterStage} onValueChange={setFilterStage}>
            <SelectTrigger className="bg-[#0A0F1C] border-[#F4E39E]/40 text-white mt-1" data-testid="filter-stage-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
              <SelectItem value="all" className="text-white">Todas las etapas</SelectItem>
              {stages.map(s => (
                <SelectItem key={s.id} value={s.id} className="text-white">{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {(() => {
        const filtered = matches.filter(m => {
          if (filterStatus !== 'all' && m.status !== filterStatus) return false;
          if (filterStage !== 'all' && m.stage?.id !== filterStage) return false;
          return true;
        });
        if (loading) return <div className="text-center text-white/50 py-10">Cargando...</div>;
        if (filtered.length === 0) return <div className="glass-card p-8 text-center text-white/40">No hay partidos que coincidan con los filtros</div>;
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4">
            {filtered.map((m, i) => (
            <div key={m.id} className="glass-card p-4" data-testid={`admin-match-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="gold-text text-xs font-semibold">{String(i + 1).padStart(2, '0')}. {m.stage?.name}</span>
                <div className="flex items-center gap-2">
                  <Badge className={m.status === 'pendiente' ? 'bg-[#F4E39E]/20 text-[#F4E39E]' : m.status === 'finalizado' ? 'bg-white/10 text-white/50' : 'bg-green-500/20 text-green-400'}>
                    {m.status}
                  </Badge>
                  {m.status === 'pendiente' && (
                    <button 
                      onClick={() => handleEdit(m)} 
                      className="p-1 rounded hover:bg-[#F4E39E]/10 text-[#F4E39E] hover:text-white transition-colors"
                      title="Editar partido"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  <button onClick={() => handleDelete(m.id)} className="p-1 rounded hover:bg-red-500/10 text-white/30 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <img src={FLAG_URL(m.home_team?.flag_code)} alt="" className="w-8 h-6 object-cover rounded" onError={(e) => e.target.style.display='none'} />
                  <span className="text-white font-bold text-sm">{m.home_team?.code}</span>
                </div>
                <span className="gold-text font-black" style={{ fontFamily: 'Montserrat' }}>VS</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-bold text-sm">{m.away_team?.code}</span>
                  <img src={FLAG_URL(m.away_team?.flag_code)} alt="" className="w-8 h-6 object-cover rounded" onError={(e) => e.target.style.display='none'} />
                </div>
              </div>
              <div className="text-white/40 text-xs mt-2 text-center">
                Fecha: {m.date} | Hora: {m.time} | Sede: {m.venue?.city}
              </div>
            </div>
          ))}
        </div>
        );
      })()}

      <Dialog open={showForm} onOpenChange={(o) => { if (!errorModal.open) setShowForm(o); }}>
        <DialogContent
          className="bg-[#111A30] border-[#F4E39E]/30 text-white max-w-md max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => { if (errorModal.open) e.preventDefault(); }}
          onInteractOutside={(e) => { if (errorModal.open) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (errorModal.open) e.preventDefault(); }}
        >
          <DialogHeader><DialogTitle className="gold-text" style={{ fontFamily: 'Montserrat' }}>{editingMatch ? 'Editar Partido' : 'Crear Nuevo Partido'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-white/60 text-sm">Etapa del torneo</label>
              <Select value={form.stage_id} onValueChange={v => setForm({ ...form, stage_id: v })}>
                <SelectTrigger className="bg-[#0A0F1C] border-[#F4E39E]/40 text-white mt-1" data-testid="match-stage-select">
                  <SelectValue placeholder="Seleccionar etapa" />
                </SelectTrigger>
                <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
                  {stages.map(s => <SelectItem key={s.id} value={s.id} className="text-white">{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/60 text-sm">Equipo 1</label>
              <Select value={form.home_team_id} onValueChange={v => setForm({ ...form, home_team_id: v, away_team_id: v === form.away_team_id ? '' : form.away_team_id })}>
                <SelectTrigger className="bg-[#0A0F1C] border-[#F4E39E]/40 text-white mt-1" data-testid="match-home-select">
                  <SelectValue placeholder="Seleccionar equipo" />
                </SelectTrigger>
                <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
                  {countries.filter(c => c.id !== form.away_team_id).map(c => <SelectItem key={c.id} value={c.id} className="text-white">{c.name} ({c.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/60 text-sm">Equipo 2</label>
              <Select value={form.away_team_id} onValueChange={v => setForm({ ...form, away_team_id: v, home_team_id: v === form.home_team_id ? '' : form.home_team_id })}>
                <SelectTrigger className="bg-[#0A0F1C] border-[#F4E39E]/40 text-white mt-1" data-testid="match-away-select">
                  <SelectValue placeholder="Seleccionar equipo" />
                </SelectTrigger>
                <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
                  {countries.filter(c => c.id !== form.home_team_id).map(c => <SelectItem key={c.id} value={c.id} className="text-white">{c.name} ({c.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            {/* Checkboxes para marcar favoritos */}
            <div className="space-y-2 p-3 bg-[#0A0F1C]/30 rounded-lg border border-[#F4E39E]/20">
              <div className="text-white/50 text-xs mb-2">Marcar Favorito (Opcional)</div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox"
                  id="favorite-home"
                  checked={form.favorite_home}
                  onChange={(e) => setForm({ ...form, favorite_home: e.target.checked, favorite_away: e.target.checked ? false : form.favorite_away })}
                  className="w-4 h-4 rounded border-[#F4E39E]/40 bg-[#0A0F1C] text-[#F4E39E] focus:ring-[#F4E39E] focus:ring-offset-0 cursor-pointer"
                />
                <label htmlFor="favorite-home" className="text-white/70 text-sm cursor-pointer">
                  Equipo 1 es favorito
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox"
                  id="favorite-away"
                  checked={form.favorite_away}
                  onChange={(e) => setForm({ ...form, favorite_away: e.target.checked, favorite_home: e.target.checked ? false : form.favorite_home })}
                  className="w-4 h-4 rounded border-[#F4E39E]/40 bg-[#0A0F1C] text-[#F4E39E] focus:ring-[#F4E39E] focus:ring-offset-0 cursor-pointer"
                />
                <label htmlFor="favorite-away" className="text-white/70 text-sm cursor-pointer">
                  Equipo 2 es favorito
                </label>
              </div>
            </div>
            
            <div>
              <label className="text-white/60 text-sm">Estadio</label>
              <Select value={form.stadium_id} onValueChange={v => setForm({ ...form, stadium_id: v })}>
                <SelectTrigger className="bg-[#0A0F1C] border-[#F4E39E]/40 text-white mt-1">
                  <SelectValue placeholder="Seleccionar estadio" />
                </SelectTrigger>
                <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
                  {stadiums.map(s => <SelectItem key={s.id} value={s.id} className="text-white">{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/60 text-sm">Sede</label>
              <Select value={form.venue_id} onValueChange={v => setForm({ ...form, venue_id: v })}>
                <SelectTrigger className="bg-[#0A0F1C] border-[#F4E39E]/40 text-white mt-1">
                  <SelectValue placeholder="Seleccionar sede" />
                </SelectTrigger>
                <SelectContent className="bg-[#111A30] border-[#F4E39E]/30">
                  {venues.map(v => <SelectItem key={v.id} value={v.id} className="text-white">{v.city}, {v.country}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/60 text-sm">Fecha</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} data-testid="match-date-input"
                  className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-3 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" />
              </div>
              <div>
                <label className="text-white/60 text-sm">Hora</label>
                <input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} data-testid="match-time-input"
                  className="w-full bg-[#0A0F1C] border border-[#F4E39E]/40 rounded-lg px-3 py-3 text-white mt-1 focus:border-[#F4E39E] outline-none" />
              </div>
            </div>
            <button onClick={handleSave} className="gold-btn w-full py-3 rounded-lg font-bold" data-testid="save-match-button">GUARDAR PARTIDO</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error Modal */}
      <ErrorModal
        open={errorModal.open}
        onClose={() => setErrorModal({ ...errorModal, open: false })}
        title={errorModal.title}
        message={errorModal.message}
      />
    </div>
  );
}
